import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import Select from "components/Shared/TranslatedSelect"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { assignOrdersToBranch } from "store/actions/orderAction"
import { showAlert } from "store/reducers/alertReducer"

const AssignToBranchModal = ({
  isOpen,
  toggle,
  selectedOrderIds = [],
  selectedOrders = [],
  branchsList = [],
  onSuccess,
}) => {
  const { t, i18n: { language } } = useTranslation()
  const dispatch = useDispatch()
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const defaultBranchId = useMemo(() => {
    const branchIds = selectedOrders
      .map(order => {
        if (!order?.branchId) return null
        return typeof order.branchId === "object"
          ? String(order.branchId._id || order.branchId)
          : String(order.branchId)
      })
      .filter(Boolean)

    if (!branchIds.length) return null

    const unique = [...new Set(branchIds)]
    return unique.length === 1 ? unique[0] : branchIds[0]
  }, [selectedOrders])

  const branchOptions = useMemo(
    () =>
      branchsList.map(branch => ({
        label: language === "sa" ? branch.nameAr : branch.name,
        value: String(branch._id),
      })),
    [branchsList, language]
  )

  useEffect(() => {
    if (!isOpen) {
      setSelectedBranch(null)
      return
    }

    const defaultOption =
      branchOptions.find(o => o.value === defaultBranchId) || null
    setSelectedBranch(defaultOption)
  }, [isOpen, defaultBranchId, branchOptions])

  const handleAssign = () => {
    if (!selectedBranch) {
      dispatch(
        showAlert({ text: t("Please select a branch"), type: "error" })
      )
      return
    }

    setSubmitting(true)
    dispatch(
      assignOrdersToBranch({
        orderIds: selectedOrderIds,
        branchId: selectedBranch.value,
      })
    )
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          onSuccess?.()
          toggle()
          setSelectedBranch(null)
        } else {
          dispatch(showAlert({ text: msg || t("Something went wrong"), type: "error" }))
        }
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{t("Assign to Branch")}</ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          {t("{{count}} order(s) selected", { count: selectedOrderIds.length })}
        </p>
        <label className="form-label">{t("Select Branch")}</label>
        <Select
          value={selectedBranch}
          options={branchOptions}
          onChange={setSelectedBranch}
          placeholder={t("Select Branch")}
        />
        <p className="small text-muted mt-3 mb-0">
          {t(
            "Reassigning to another branch will clear any existing rider assignment on these orders."
          )}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={submitting}>
          {t("Cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleAssign}
          disabled={submitting || !selectedBranch}
        >
          {submitting ? <Spinner size="sm" /> : t("Assign")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AssignToBranchModal
